import { useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useEffect } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { unauthorizedEvent } from '../../lib/api';
import { queryKeys } from '../../lib/queryClient';
import { getCurrentSession } from './authApi';
import { AuthContext, useAuth } from './authContext';
import type { Capability } from '../../types';

export function AuthProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const session = useQuery({
    queryKey: queryKeys.me,
    queryFn: getCurrentSession,
    retry: false,
    staleTime: 15_000,
  });

  useEffect(() => {
    const handleUnauthorized = () => {
      queryClient.setQueryData(queryKeys.me, { authenticated: false, needsSetup: false });
      navigate('/login', { replace: true });
    };
    window.addEventListener(unauthorizedEvent, handleUnauthorized);
    return () => window.removeEventListener(unauthorizedEvent, handleUnauthorized);
  }, [navigate, queryClient]);

  const data = session.data;
  const user = data?.authenticated ? data.user : null;
  const needsSetup = data?.authenticated === false ? data.needsSetup : false;

  return (
    <AuthContext.Provider
      value={{
        user,
        needsSetup,
        loading: session.isPending,
        error: session.error,
        refresh: async () => { await session.refetch(); },
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading, error } = useAuth();
  const location = useLocation();

  if (loading) return <AuthStatus message="Checking your session" />;
  if (error) return <AuthStatus message="Could not reach ModIQ" detail="Refresh the page to try again." />;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  return children;
}

export function RequireCapability({ anyOf, children }: { anyOf: Capability[]; children: ReactNode }) {
  const { user } = useAuth();
  if (!user) return null;
  if (anyOf.some(capability => user.caps[capability])) return children;
  return <Navigate to="/home" replace />;
}

export function AuthStatus({ message, detail }: { message: string; detail?: string }) {
  return (
    <main className="auth-status" role="status">
      <span className="auth-status__pulse" />
      <strong>{message}</strong>
      {detail ? <span>{detail}</span> : null}
    </main>
  );
}
